import {
  useDownloadTransactionRowLevelDetails,
  useGetOneTransaction,
  useGetTransactionDetailPage,
  useOriginalSubmissionFileDownload,
} from "#hooks/adapters/useTransactions";
import { useCursorSortingAndPagination } from "#redux/reducers/tableStateReducer";
import {
  extractTransactionItemData,
  getTransactionDetailStatusPillToggleVariant,
  getTransactionDetailStatusPillVariant,
} from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionDetail.helpers";
import { DataSetSubmissionErrorDrawer } from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionErrorDrawer";
import styles from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionRowTable.module.scss";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { DataSetSubmissionRowTableErrorDataDisplay } from "#src/routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionRowTableErrorDataDisplay";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonWithPopover,
  Checkbox,
  DataTable,
  DataTablePanel,
  Dialog,
  DropdownMenu,
  EmptyState,
  HeaderType,
  Icon,
  IconLoading,
  Link,
  Panel,
  Pill,
  PillToggleGroup,
  TextDataDisplay,
  useSort,
  useToast,
  type EmptyStateProps,
  type PanelProps,
} from "@validereinc/common-components";
import {
  Resources,
  SortDirection,
  TransactionAdapter,
  TransactionDetailItemStatus,
  TransactionDetailItemStatusType,
  TransactionStatus,
  type TransactionType,
} from "@validereinc/domain";
import {
  Transactions,
  type DataSetSubmissionTabularDataRowType,
} from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";

const cx = classNames.bind(styles);

const { selectFirstAndLastTransactionState } = Transactions.logic;
const {
  DataSetSubmissionTabularDataCellStyleVariantsMap,
  getDataSetSubmissionTabularCellError,
  getReadableDataSetSubmissionErrorType,
  getReadableTransactionErrorDetails,
  getStepDisplayLabel,
  getTransactionDetailStatusDisplayLabelBasedOnStep,
  getTransactionDetailStatusesBasedOnStep,
  getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow,
  getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow,
} = Transactions.view;

const DEFAULT_FILTER_KEY = "all";

export const DataSetSubmissionRowTable = ({
  transactionId,
  panelProps,
  transactionStep,
  transactionStatus,
}: {
  transactionId: string | undefined;
  panelProps?: Omit<PanelProps, "children">;
  transactionStep: TransactionType["step"] | undefined;
  transactionStatus: TransactionType["status"] | undefined;
}) => {
  const [tableConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["table-configuration", "table-state"],
    ["dataset", "submission", transactionId ?? "", "table"]
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const transactionDetailStatuses = useMemo(
    () => getTransactionDetailStatusesBasedOnStep(transactionStep),
    [transactionStep]
  );
  const [isRowLevelDetailShown, setIsRowLevelDetailShown] = useState(false);
  const [isRetryConfirmationOpen, setIsRetryConfirmationOpen] = useState(false);
  const [shouldRetryAllRows, setShouldRetryAllRows] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(DEFAULT_FILTER_KEY);
  const [cachedValidationStepCounts, setCachedValidationStepCounts] = useState<{
    successCount: number;
    failureCount: number;
  } | null>(null);
  const [errorDrawerIndex, setErrorDrawerIndex] = useState<number>();

  const statusFilterParam: TransactionDetailItemStatusType | undefined =
    statusFilter === DEFAULT_FILTER_KEY
      ? undefined
      : (statusFilter as TransactionDetailItemStatusType);
  const {
    tableState,
    updateSorting,
    updatePagination,
    registerNextPageToken,
    continuationToken,
    availablePages,
  } = useCursorSortingAndPagination(
    {
      sortBy: "index",
      sortDirection: SortDirection.ASCENDING,
    },
    { statusFilter: statusFilterParam, transactionId },
    tableStateStorageKey
  );

  const handleStatusFilterChange = (newSelection: string[]) => {
    const newlySelected = newSelection.find((value) => value !== statusFilter);
    setStatusFilter(newlySelected ?? DEFAULT_FILTER_KEY);
    updatePagination({ page: 1, pageSize: tableState.pageSize });
  };

  const transactionQuery = useGetOneTransaction({
    id: transactionId ?? "",
    meta: { history: true },
  });
  const transactionLastStepQuery = useGetOneTransaction(
    {
      id: transactionId ?? "",
    },
    {
      enabled: !!transactionQuery.error,
    }
  );
  const { firstTransaction, lastTransaction } =
    selectFirstAndLastTransactionState(
      transactionQuery.data?.data ?? transactionLastStepQuery.data?.data
    );
  const {
    mutate: downloadTransactionRowLevelDetails,
    isLoading: areTransactionRowLevelDetailsDownloading,
  } = useDownloadTransactionRowLevelDetails();
  const originalFileSubmissionDownloadMutation =
    useOriginalSubmissionFileDownload();
  const {
    data: pageQuery,
    isLoading: isLoadingTransactionRowLevelDetails,
    isFetching: isFetchingTransactionRowLevelDetails,
    error: errorTransactionRowLevelDetails,
    refetch: refetchTransactionRowLevelDetails,
  } = useGetTransactionDetailPage({
    transactionId,
    statusFilter: statusFilterParam,
    pageSize: tableState.pageSize,
    continuationToken,
  });
  const pageData = pageQuery?.data;

  useEffect(() => {
    registerNextPageToken(tableState.page, pageData?.continuation_token);
  }, [tableState.page, pageData?.continuation_token, registerNextPageToken]);

  useEffect(() => {
    if (
      transactionStep !== "archiver" ||
      statusFilter !== DEFAULT_FILTER_KEY ||
      pageData?.success_count == null ||
      pageData?.failure_count == null
    ) {
      return;
    }

    setCachedValidationStepCounts({
      successCount: pageData.success_count,
      failureCount: pageData.failure_count,
    });
  }, [
    transactionStep,
    statusFilter,
    pageData?.success_count,
    pageData?.failure_count,
  ]);

  /**
   * The "validation" (archiver) step doesn't get a `status_counts` breakdown on the transaction
   * object, so its two possible statuses (successful/failed) fall back to the cached detail-page
   * aggregate counts. Every other step's statuses are counted via `status_counts`.
   */
  const getStatusCount = (
    status: TransactionDetailItemStatusType
  ): number | undefined => {
    if (transactionStep === "archiver") {
      return status === TransactionDetailItemStatus.FAILED
        ? cachedValidationStepCounts?.failureCount
        : cachedValidationStepCounts?.successCount;
    }

    return lastTransaction?.status_counts?.[status] ?? 0;
  };

  const isExpiredTransaction =
    !!transactionQuery.error && !!transactionLastStepQuery.data?.data[0];
  const canRetryStep =
    (transactionStatus === TransactionStatus.FAILED ||
      transactionStatus === TransactionStatus.COMPLETED_WITH_PARTIAL_SUCCESS) &&
    (transactionStep === "operator" || transactionStep === "sink");

  const retryTransaction = useMutation({
    mutationFn: (
      params: Parameters<typeof TransactionAdapter.retry.createOne>[0]
    ) => {
      return TransactionAdapter.retry.createOne(params);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: [Resources.TRANSACTION] });
    },
    onError: (err) => {
      ExceptionUtils.reportException(err, "error", {
        hint: "Failed to re-try step in dataset submissions details page",
      });
      toast.push({
        description: "Failed to re-try step.",
        intent: "error",
      });
    },
  });

  const pageRows: DataSetSubmissionTabularDataRowType[] = (
    pageData?.details ?? []
  ).map((item) => ({
    ...item,
    index: parseInt(item.index), // Casting is necessary for client-side sorting using esToolkit.orderBy
    data: item.data ?? "",
    error: item.error ?? "",
    error_msg: item.error_msg ?? "",
    entity_type: item.entity_type ?? "",
    error_details: item.error_details ?? [],
  }));
  const { data: items } = useSort(pageRows, {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
  });

  /**
   * During the final ingestion step ("sink"), counts and totals are not
   * meaningful while the step is still in-progress (pending). Once the step
   * reaches any terminal state - completed, partially completed, or failed -
   * the counts reflect real data and can be shown.
   */
  const isFinalIngestionStep = transactionStep === "sink";
  const isTransactionInProgress =
    transactionStatus === TransactionStatus.PENDING;
  const shouldShowCountsAndTotal =
    !isFinalIngestionStep || !isTransactionInProgress;

  const pagination = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    total: shouldShowCountsAndTotal
      ? statusFilterParam === undefined
        ? (lastTransaction?.total_count ?? undefined)
        : getStatusCount(statusFilterParam)
      : undefined,
    hasNextPage: !!pageData?.continuation_token,
    availablePages,
  };

  const getEmptyStateProps = (): EmptyStateProps | undefined => {
    const errorDetails = getReadableTransactionErrorDetails(
      lastTransaction?.details
    );
    const retryAction = canRetryStep ? (
      <ButtonWithPopover
        buttonProps={{
          icon: "arrow-counter-clockwise",
          variant: "primary",
          onClick: () => {
            setShouldRetryAllRows(false);
            setIsRetryConfirmationOpen(true);
          },
          isLoading: retryTransaction.isLoading,
          disabled: isExpiredTransaction,
        }}
        label="Can retry failed or all rows"
      >
        Retry
      </ButtonWithPopover>
    ) : undefined;
    const viewDataAction = (
      <Button
        variant="primary"
        onClick={() => {
          queryClient.refetchQueries({
            queryKey: [Resources.TRANSACTION],
          });
          setIsRowLevelDetailShown(true);
        }}
        isLoading={isLoadingTransactionRowLevelDetails}
        slotLeft={
          errorTransactionRowLevelDetails ? (
            <Icon variant="warning-octagon" />
          ) : null
        }
      >
        View Data
      </Button>
    );
    const actionsForStepsWithPotentialData = (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {viewDataAction}
      </div>
    );

    switch (lastTransaction?.step) {
      case "transaction_recorder":
      case "vulnerability_scanner": {
        const stepDisplayLabel = getStepDisplayLabel("transaction_recorder");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion: "Recording and scanning the submission...",
            };
          default:
            return;
        }
      }
      case "archiver": {
        const stepDisplayLabel = getStepDisplayLabel("archiver");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
              action: actionsForStepsWithPotentialData,
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion: "Validating and backing up the submission...",
            };
          default:
            return;
        }
      }
      case "operator": {
        const stepDisplayLabel = getStepDisplayLabel("operator");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
              action: retryAction,
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion:
                "The transformations applied are determined by the target Dataset.",
            };
          default:
            return;
        }
      }
      case "sink": {
        const stepDisplayLabel = getStepDisplayLabel("sink");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
              action: actionsForStepsWithPotentialData,
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion: "Ingesting the submission data into the database...",
            };
          case TransactionStatus.COMPLETED: {
            const successCount =
              lastTransaction?.successful_count ?? pageData?.success_count;

            return {
              variant: "success",
              icon: <Icon variant="seal-check" />,
              title: `Successfully completed ${stepDisplayLabel}`,
              suggestion: `${successCount !== undefined ? `${successCount} rows have been` : "The submission data has been"} ingested into the database.`,
              action: viewDataAction,
            };
          }
          case TransactionStatus.COMPLETED_WITH_PARTIAL_SUCCESS: {
            const successCount =
              lastTransaction?.successful_count ?? pageData?.success_count;
            const failureCount =
              lastTransaction?.status_counts?.[
                TransactionDetailItemStatus.FAILED
              ] ?? pageData?.failure_count;

            return {
              variant: "warning",
              icon: <Icon variant="seal-check" />,
              title: `Partially completed ${stepDisplayLabel}`,
              suggestion: `${successCount !== undefined && failureCount !== undefined ? `${successCount} rows have been successfully ingested and ${failureCount} rows have not been ingested into the database.` : "The submission data has been partially ingested into the database."} View data to see more details.`,
              action: viewDataAction,
            };
          }
          default:
            return;
        }
      }
      default:
        return;
    }
  };

  useEffect(() => {
    if (!pageData?.details?.length) return;

    setIsRowLevelDetailShown(true);
  }, [pageData]);

  useEffect(() => {
    if (lastTransaction?.step !== "sink") return;
    if (lastTransaction.state === "pending") return;

    refetchTransactionRowLevelDetails();
    setIsRowLevelDetailShown(true);
  }, [lastTransaction, refetchTransactionRowLevelDetails]);

  // Assume the first row contains all the headers
  const parsedHeaders: Array<HeaderType<DataSetSubmissionTabularDataRowType>> =
    Object.keys(extractTransactionItemData(items[0]?.data ?? "{}")).map(
      (label) => ({
        key: `data.${label}`,
        label: label,
        renderComponent: ({ item, isLoading, isBusy }) => {
          const errDetails = getDataSetSubmissionTabularCellError(item, label);
          const data = extractTransactionItemData(item.data ?? "{}");

          if (item.status === "failed" && errDetails) {
            return (
              <DataSetSubmissionRowTableErrorDataDisplay
                isLoading={isLoading}
                isBusy={isBusy}
                intent="error"
                errorType={getReadableDataSetSubmissionErrorType(
                  errDetails.validation_type
                )}
                errorMessage={errDetails.error_message}
                errorSuggestions={errDetails.validation_value ?? ""}
              >
                <TextDataDisplay
                  isLoading={isLoading}
                  isBusy={isBusy}
                  value={data[label] as string}
                  style={{
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                />
              </DataSetSubmissionRowTableErrorDataDisplay>
            );
          }

          return (
            <DataTable.DataRow.TextCell
              isLoading={isLoading}
              isBusy={isBusy}
              value={data[label] as string}
              style={{
                overflowWrap: "break-word",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
              }}
            />
          );
        },
        getStyle: ({ item }) => {
          const styleVariantToApply =
            getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow(
              item,
              label
            );

          if (styleVariantToApply === "default") return {};

          return {
            backgroundColor:
              DataSetSubmissionTabularDataCellStyleVariantsMap[
                `${styleVariantToApply}_light`
              ],
            color:
              DataSetSubmissionTabularDataCellStyleVariantsMap[
                `${styleVariantToApply}_dark`
              ],
            fontWeight: "bold",
          };
        },
      })
    );

  const headers: Array<HeaderType<DataSetSubmissionTabularDataRowType>> = [
    {
      key: "index",
      label: "Row",
      isSortable: true,
      isFixed: true,
      renderComponent: ({ item }) => {
        if (item.error) {
          const styleVariantToApply =
            getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow(item);

          return (
            <Link
              anchorTagProps={{
                style:
                  styleVariantToApply === "default"
                    ? {}
                    : {
                        color:
                          DataSetSubmissionTabularDataCellStyleVariantsMap[
                            `${styleVariantToApply}_dark`
                          ],
                        fontWeight: "bold",
                      },
              }}
              label={`#${item.index + 1}`}
              onClick={() => {
                setErrorDrawerIndex(
                  errors.findIndex(({ index }) => index == item.index)
                );
              }}
            />
          );
        } else {
          return `#${item.index + 1}`;
        }
      },
      getStyle: ({ item }) => {
        const styleVariantToApply =
          getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow(item);

        if (styleVariantToApply === "default") return {};

        return {
          backgroundColor:
            DataSetSubmissionTabularDataCellStyleVariantsMap[
              `${styleVariantToApply}_light`
            ],
          color:
            DataSetSubmissionTabularDataCellStyleVariantsMap[
              `${styleVariantToApply}_dark`
            ],
          fontWeight: "bold",
        };
      },
    },
    {
      key: "status",
      label: "Status",
      isSortable: true,
      isFixed: true,
      renderComponent: ({ item }) => (
        <Pill variant={getTransactionDetailStatusPillVariant(item.status)}>
          {getTransactionDetailStatusDisplayLabelBasedOnStep(
            item.status,
            transactionStep
          )}
        </Pill>
      ),
    },
    ...parsedHeaders,
  ];

  const hasAnyRows = (lastTransaction?.total_count ?? 0) > 0;
  const pillToggles =
    hasAnyRows || statusFilter !== DEFAULT_FILTER_KEY
      ? [
          {
            name: DEFAULT_FILTER_KEY,
            label: "All",
            value: DEFAULT_FILTER_KEY,
            isSelected: statusFilter === DEFAULT_FILTER_KEY,
            shouldSelectAll: true,
            count: shouldShowCountsAndTotal
              ? (lastTransaction?.total_count ?? undefined)
              : undefined,
          },
          ...transactionDetailStatuses.map((status) => ({
            name: status,
            label: getTransactionDetailStatusDisplayLabelBasedOnStep(
              status,
              transactionStep
            ),
            value: status,
            variant: getTransactionDetailStatusPillToggleVariant(status),
            isSelected: statusFilter === status,
            count: shouldShowCountsAndTotal
              ? getStatusCount(status)
              : undefined,
          })),
        ]
      : [];

  const emptyStateProps = getEmptyStateProps();
  const errors = items.filter(
    ({ status }) => status === TransactionDetailItemStatus.FAILED
  );

  const actionRow = [
    <Button
      key="summary"
      variant="outline"
      icon="eye"
      onClick={() => setIsRowLevelDetailShown(false)}
    >
      View Summary
    </Button>,
    ...(hasAnyRows
      ? [
          <DropdownMenu
            key="download-opts"
            contentProps={{
              align: "end",
            }}
            options={[
              {
                label:
                  transactionStep === "operator" || transactionStep === "sink"
                    ? `Failed transformed rows`
                    : `Failed rows`,
                isDisabled:
                  originalFileSubmissionDownloadMutation.isLoading ||
                  !transactionId,
                onClick: () => {
                  if (!transactionId) return;

                  originalFileSubmissionDownloadMutation.mutate(
                    {
                      transactionId,
                      fileName: firstTransaction?.original_file_name ?? "",
                      onlyErrors: true,
                      hasTransformed:
                        transactionStep === "operator" ||
                        transactionStep === "sink"
                          ? true
                          : false,
                    },
                    {
                      onError: () => {
                        if (!isExpiredTransaction) return;

                        toast.push({
                          intent: "error",
                          description:
                            "Could not download submission because the transaction is expired (its older than 90 days)",
                        });
                      },
                    }
                  );
                },
              },
              {
                label: "Failed rows with error details",
                isDisabled:
                  areTransactionRowLevelDetailsDownloading || !transactionId,
                onClick: () => {
                  if (!transactionId) return;

                  downloadTransactionRowLevelDetails({
                    id: transactionId,
                    fileName: `dataset_submission_${transactionId}_row_level_details.csv`,
                  });
                },
              },
            ]}
          >
            <Button
              icon="arrow-square-out"
              slotRight={<Icon variant="caret-down" />}
              variant="outline"
              isLoading={
                originalFileSubmissionDownloadMutation.isLoading ||
                areTransactionRowLevelDetailsDownloading
              }
            >
              Export
            </Button>
          </DropdownMenu>,
          ...(canRetryStep
            ? [
                <ButtonWithPopover
                  key="resubmit"
                  buttonProps={{
                    icon: "arrow-counter-clockwise",
                    variant: "primary",
                    onClick: () => {
                      setShouldRetryAllRows(false);
                      setIsRetryConfirmationOpen(true);
                    },
                    isLoading: retryTransaction.isLoading,
                    disabled: isExpiredTransaction,
                  }}
                  label="Can retry failed or all rows"
                >
                  Retry
                </ButtonWithPopover>,
              ]
            : []),
        ]
      : []),
  ];

  return (
    <>
      <DataSetSubmissionErrorDrawer
        items={errors}
        itemIndex={errorDrawerIndex}
        setItemIndex={setErrorDrawerIndex}
      />
      <Dialog
        isOpen={isRetryConfirmationOpen}
        onClose={() => {
          setIsRetryConfirmationOpen(false);
          setShouldRetryAllRows(false);
        }}
        title="Retry step?"
        actionRow={[
          <Button
            key="retry"
            variant="primary"
            onClick={() => {
              if (
                !transactionId ||
                !transactionStep ||
                (transactionStep !== "operator" && transactionStep !== "sink")
              )
                return;

              const transactionRetryStage =
                transactionStep === "operator" ? "transformation" : "ingestion";

              toast
                .pushAsync(
                  retryTransaction.mutateAsync({
                    transactionId,
                    transactionRetryStage,
                    retryAll: shouldRetryAllRows,
                  }),
                  {
                    intent: "info",
                    description: `Re-trying ${getStepDisplayLabel(transactionStep)}`,
                    isBusy: true,
                  },
                  {
                    isDismissible: false,
                  }
                )
                .then(({ toast: firstToast }) =>
                  toast.push(
                    {
                      intent: "success",
                      isBusy: false,
                      description: `Successfully re-tried ${getStepDisplayLabel(transactionStep)}.`,
                    },
                    {
                      id: firstToast.id,
                      isDismissible: true,
                    }
                  )
                );
              setIsRetryConfirmationOpen(false);
              setShouldRetryAllRows(false);
            }}
          >
            Yes, Retry
          </Button>,
        ]}
      >
        <p>
          Resubmitting a partial or failed dataset will overwite old rows. Do
          you want to continue?
        </p>
        <Checkbox
          name="retry-all-rows"
          value={shouldRetryAllRows}
          onChange={setShouldRetryAllRows}
          label="Retry all rows? (Includes rows that previously succeeded)"
        />
      </Dialog>
      {isRowLevelDetailShown ? (
        <DataTablePanel
          storageKey={tableConfigStorageKey}
          panelProps={{
            title: transactionStep
              ? getStepDisplayLabel(transactionStep)
              : "Submission Data",
            titleDecorator: (
              <div className={cx("titleDecorator")}>
                <PillToggleGroup
                  name="status"
                  pills={pillToggles}
                  onChange={handleStatusFilterChange}
                  isLoading={isLoadingTransactionRowLevelDetails}
                />
                {shouldShowCountsAndTotal && pageData?.total_entries != null ? (
                  <span className={cx("pageSummary")}>
                    {pageData.total_entries} entries
                    {pageData.total_pages != null
                      ? ` · page ${tableState.page} of ${pageData.total_pages}`
                      : ""}
                  </span>
                ) : null}
              </div>
            ),
            actionRow,
            ...panelProps,
          }}
          dataTableProps={{
            headers,
            items,
            getItemId: (item) => item.index.toString(),
            isLoading: isLoadingTransactionRowLevelDetails,
            isBusy:
              isFetchingTransactionRowLevelDetails &&
              !isLoadingTransactionRowLevelDetails,
            onSortChange: updateSorting,
            onPaginationChange: updatePagination,
            sorting: {
              sortBy: tableState.sortBy,
              sortDirection: tableState.sortDirection,
            },
            pagination,
            emptyStateProps: errorTransactionRowLevelDetails
              ? {
                  title: "No rows available",
                  variant: "error",
                  suggestion: "Row-level error information is not available",
                }
              : {
                  title: "No rows available",
                  suggestion: "Try adjusting the filters",
                },
          }}
        />
      ) : (
        <Panel
          title={
            transactionStep
              ? getStepDisplayLabel(transactionStep)
              : "Step Details"
          }
          loaded={!isLoadingTransactionRowLevelDetails}
        >
          <EmptyState
            {...(emptyStateProps ?? {
              title: "Submission in-progress",
              variant: "info",
            })}
          />
        </Panel>
      )}
    </>
  );
};
